import React, { useState, useRef, useEffect, useCallback } from 'react';
import { SendOutlined, AudioOutlined } from '@ant-design/icons';
import useSpeechRecognition from '../../hooks/useSpeechRecognition';

const ChatInput = ({ onSend, disabled, placeholder = 'Sorunuzu yazın...' }) => {
  const [message, setMessage] = useState('');
  const inputRef = useRef(null);
  const preRecordTextRef = useRef('');

  useEffect(() => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
    }
  }, [disabled]);

  const handleSpeechResult = useCallback(({ finalTranscript, interimTranscript }) => {
    const base = preRecordTextRef.current;
    const separator = base && !base.endsWith(' ') ? ' ' : '';

    if (finalTranscript) {
      const newText = base + separator + finalTranscript;
      preRecordTextRef.current = newText;
      setMessage(newText);
    } else if (interimTranscript) {
      setMessage(base + separator + interimTranscript);
    }
  }, []);

  const {
    isSupported,
    isListening,
    error: speechError,
    toggleListening,
    stopListening,
  } = useSpeechRecognition({ onResult: handleSpeechResult });

  const handleMicClick = () => {
    if (!isListening) {
      preRecordTextRef.current = message;
    }
    toggleListening();
    inputRef.current?.focus();
  };

  const handleSend = () => {
    if (message.trim() && !disabled) {
      if (isListening) {
        stopListening();
      }
      onSend(message);
      setMessage('');
      preRecordTextRef.current = '';
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const displayPlaceholder = speechError || (isListening ? 'Dinleniyor...' : placeholder);

  return (
    <div className="chat-input-area">
      <input
        ref={inputRef}
        className={`chat-input${isListening ? ' chat-input-recording' : ''}`}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={displayPlaceholder}
        disabled={disabled}
        autoFocus
      />
      {isSupported && (
        <button
          className={`chat-mic-btn${isListening ? ' recording' : ''}`}
          onClick={handleMicClick}
          disabled={disabled}
          aria-label={isListening ? 'Dinlemeyi durdur' : 'Sesle yaz'}
          type="button"
        >
          <AudioOutlined />
        </button>
      )}
      <button
        className="chat-send-btn"
        onClick={handleSend}
        disabled={disabled || !message.trim()}
        aria-label="Gönder"
      >
        <SendOutlined />
      </button>
    </div>
  );
};

export default ChatInput;
